import { useState } from "react"

import { Datasets } from "../../../../../constants/enums"
import { SUPPORT_REFER } from "../../../../../errors/errorMessages"
import { AppError } from "../../../../../errors/errorProcessors"
import { invalidApiDataError } from "../../../../../errors/recognisedErrors"
import { snackbar } from "../../../../../utils/general/generalUtils"
import { deleteClientProtected } from "../../../clients/clients"
import {
    DELETE_LCT_ATTACHMENT,
    DELETE_TTR_ATTACHMENT,
} from "../../../urls/private"

interface IUseDeleteUploadedAttachmentOutput {
    deleteUploadedAttachment: (
        deviceRef?: string,
        fileName?: string,
        attachmentUuid?: string
    ) => Promise<void>
    isLoading: boolean
    isLoaded: boolean
    error?: AppError
}

/** Hook provides a function for deleting an uploaded device attachment.
 * Used for both TTR and LCT devices.
 */
const useDeleteUploadedAttachment = (
    isLct: boolean,
    dataset: Datasets,
    isAmendment?: boolean
): IUseDeleteUploadedAttachmentOutput => {
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [error, setError] = useState<AppError | undefined>(undefined)
    const [isLoaded, setIsLoaded] = useState<boolean>(false)

    const deleteUploadedAttachment = async (
        deviceRef?: string,
        fileName?: string,
        attachmentUuid?: string
    ): Promise<void> => {
        let url = ""
        if (isLct) {
            if (!fileName || !attachmentUuid) {
                return
            }
            url = DELETE_LCT_ATTACHMENT(
                fileName,
                dataset,
                attachmentUuid,
                isAmendment
            )
        } else {
            if (!deviceRef || !fileName) {
                return
            }
            url = DELETE_TTR_ATTACHMENT(deviceRef, fileName, attachmentUuid)
        }

        setError(undefined)
        setIsLoading(false)
        setIsLoaded(false)

        try {
            setIsLoading(true)
            await deleteClientProtected(url)
            snackbar("Successfully deleted attachment", "success")
            setIsLoaded(true)
        } catch {
            const appError = invalidApiDataError("Couldn't delete attachment")
            setError(appError)
            snackbar(
                `Couldn't delete attachment, please try again. ${SUPPORT_REFER}`,
                "warning"
            )
        } finally {
            setIsLoading(false)
        }
    }

    return {
        deleteUploadedAttachment,
        isLoaded,
        isLoading,
        error,
    }
}

export default useDeleteUploadedAttachment
